<template lang='pug'>
div.fill-height.main-container(ref="contentContainer")
  div.empty-content.text-h4(v-if="!isUnitySupported")
    p {{unitySupportMessage}}
    canvas(ref='emptyCanvas' width=1 height=1)
  div.editor-interface(v-else ref="editorContainer" :class="{ 'stack-inner': stackEditor }")
    //- TODO Disable when dialog is showing over on small screen?
    EditorActionBar.editor-actions(
                    :isHorizontalView="stackEditor"
                    :isLoading="isUnityLoading || isOpeningLoad"
                    :isEditMode="isEditScene"
                    :isPoseMode="isPoseScene"
                    :hasUnsavedChanges="hasUnsavedChanges"
                    :isBackendBusy="isUpdatingData"
                    :isPendingBuild="isBuildQueued"
                    :isProcessingBuild="!isBuildQueued && (isProcessingBuild || !!pendingBuildAction)"
                    :isPendingShare="isPendingShare"
                    :isSharing="isSharingCharacter"
                    :shareLevel="shareLevel"
                    :isQueryingShareLevel="!!queryingShareId"
                    :showAccount="!isAppBarVisible"
                    :isAccountPaid="isAccountPaid"
                    @info-action="onInfoDialogAction"
                    @save-changes="saveChanges('actions')"
                    @mailing-list="onMailingListDialogAction"
                    @toggle-build-pose="onToggleBuildPoseAction(true)"
                    @start-download="onStartDownloadAction"
                    @preview-generated="onPreviewGeneratedAction"
                    @restore-dimensions="onRestoreAction"
                    @share-character="onShareCharacter"
                    @estimate-pose="onEstimatePose"
                    @open-account-pay="openAccountPay"
                    @open-character-browser="openCharacterBrowser")
    Unity3dContainer.unity-container(
                    ref='unityContainer'
                    buildPathUri="/unityBuild/day6-editor"
                    :background="unityCanvasBackground"
                    companyName="quickytools"
                    productName="qharacter"
                    :canvasOpacity="unityCanvasLoadOpacity"
                    freeInput
                    @load-progress="onUnityLoading"
                    @load-complete="onUnityLoaded"
                    @unity-start-error="onUnityError"
                    @script-load-error="onUnityError")
      div#canvas-interior
        v-progress-circular.center-loading-indicator(
                            v-if="isUnityLoading"
                            :color='loadingIndicatorUi.color'
                            :width='loadingIndicatorUi.size'
                            indeterminate)
        v-color-picker#color-picker(
                      v-if="isColorPickerVisible"
                      v-model="selectedColor"
                      :class="{ 'bottom-gravity': isSmallScreen, 'top-space': !isSmallScreen && isPreviewScene }"
                      :canvas-height="colorPickerHeight"
                      :width="colorPickerWidth"
                      :hide-inputs="isSmallScreen")
      CharacterPoseSequencesView.pose-sequences-view(
                                ref="poseSequencesView"
                                v-if="enableAnimationFeatures"
                                v-model="characterAnimationsCache"
                                :hasChanges="isPoseChanged"
                                :isSavingChanges="isSavingData"
                                :isSavingNew="isSavingNewPoseSequence"
                                :isSavingName="isSavingPoseSequenceName"
                                :isDeletingSequence="isDeletingPoseSequence"
                                :isLoadingSequence="isLoadingAnimations || isLoadingPoseSequence"
                                :activeSequenceId="activePoseSequenceId"
                                :nameChangingId="poseSequenceNameChangingId"
                                :deletingId="poseSequenceDeletingId"
                                @on-close-view="focusUnity"
                                @save-changes="saveChanges('pose')"
                                @open-pose-sequences="onOpenPoseSequences"
                                @load-pose-sequence="onLoadPoseSequence"
                                @new-pose-sequence="onNewPoseSequence"
                                @update-pose-sequence-name="onUpdatePoseSequenceName"
                                @delete-pose-sequence="onDeletePoseSequence")
      div(v-if="isKeyframeTimelineVisible && enableAnimationFeatures").keyframe-timeline-container
        div.keyframe-timeline-inner
          KeyframeTimeline.keyframe-timeline(
                          ref='keyframeTimeline'
                          v-model="animationKeyframes"
                          :frameRange='timelineViewFrameRange'
                          :canvas-padding="8"
                          @on-frame-range-change="onKeyframeRangeChange"
                          @on-active-frame-index="onActiveKeyframeIndex"
                          @shift-frames="onShiftKeyframes"
                          @delete-frames="onDeleteKeyframes"
                          @toggle-play-pause="unityTogglePlayPause"
                          @keyboard-navigate="unityPoseSequenceNavigate")
      div.preview-playback-container(v-if="isPreviewScene && showPreviewPlayback")
        div.preview-playback-inner
          div.preview-playback-actions
            v-btn(icon color='accent' @click="onPreviewRewind")
              v-icon {{mdiRewind}}
            v-btn(icon color='accent' @click="onPreviewTogglePlayPause")
              v-icon {{isPreviewPlayingAnimation ? mdiPause : mdiPlay}}
  FullDialog(ref='fullDialog' @dialog-action='onDialogAction' @on-close='onDialogClose')
  Day6Info(v-model="showInfoDialog" :isTouchMode="showTouchInfoUi")
  MailingListDialog(v-model="showMailingListDialog"
                    list-key="day6-marketing"
                    :title='day6Resource.day6Updates')
  v-dialog(v-model='showPoseEstimateDialog'
          :loading='isEstimatingPose'
          max-width="360")
    v-card
      ImagePoseProvider.pa-4(
                        @is-estimating="isEstimatingPose=true"
                        @pose-data="onPoseEstimateResult")
      v-card-actions
        v-spacer
        v-btn(outlined
              color='accent'
              @click.native='onEstimatePoseAction(-1)') {{commonResource.cancel}}
        v-btn(outlined
              color='accent'
              @click.native='onEstimatePoseAction(0)') {{commonResource.close}}
  v-dialog(v-model='showDataBrowser'
          :fullscreen="isSmallScreen"
          :hide-overlay="isSmallScreen"
          max-width="800")
    //- TODO Set height exactly when actions are stacked below and full screen?
    div.data-browser-frame
      CharacterModelBrowser(ref="characterBrowser"
                            :character-id-summary="dimensionsIdSummary"
                            :has-changes="hasUnsavedChanges"
                            :is-saving-changes="isSavingData"
                            :is-loading="isLoadingCharacter"
                            @save-changes="saveChanges('character')"
                            @new-character="onBrowserNewCharacter"
                            @load-character="onBrowserLoadCharacter"
                            @on-character-delete="onCharacterDelete"
                            @close-browser="showDataBrowser=false")
  v-dialog(v-model='showPersistentDialog'
          persistent
          max-width="400")
    div.loading-modal.d-flex.flex-row.align-center
      v-progress-circular(:color='loadingIndicatorUi.color'
                          :width='loadingIndicatorUi.size'
                          indeterminate)
      p.body-1.ml-4.mb-0 {{persistentDialogMessage}}
  v-dialog(v-model='showAssetBuildTimeEstimateDialog'
          max-width="400")
    v-card
      v-card-title {{day6Resource.buildTimeEstimate}}
      v-card-text
        p.body-1 {{day6Resource.assetBuildEstimate()}}
        v-checkbox(v-model="hideBuildTimeEstimateAlert"
                  :label='day6Resource.hideFutureMessage'
                  color='accent'
                  :disabled='hideBuildTimeEstimateAlert')
      v-card-actions
        v-spacer
        v-btn(color='accent' text @click="showAssetBuildTimeEstimateDialog=false") {{commonResource.ok}}
  v-snackbar(v-model="snackbar"
            :timeout="snackbarTimeout"
            color="accent"
            :centered="isSmallScreen"
            :top="!isSmallScreen"
            rounded="pill") {{ snackbarText }}
</template>

<script>
import Vue from 'vue'
import day6Resource from '@/resources/day6'
import commonResource from '@/resources/common'
import poseEstimateResource from '@/resources/poseEstimate'
import uiConstant from '@/resources/uiConstant'
import routes from '@/router/routes'
import EditorActionBar from './day6-editor-action-bar'
import localDefaultBodyDimensions from './localDefaultBodyDimensions.js'
import Unity3dContainer from '../unity3d/unity3d-container'
import FullDialog from '../dialogs/title-message-action-dialog'
import ImagePoseProvider from '../poseEstimate/image-pose-provider.vue'
import Day6Info from '../day6/infos/day6-info.vue'
import MailingListDialog from '../mailingList/mailing-list-add.vue'
import CharacterModelBrowser from './day6-data-browsers/character-model-browser.vue'
import CharacterPoseSequencesView from './day6-data-browsers/character-pose-sequences-view.vue'
import KeyframeTimeline from '../keyframeTimeline/keyframe-timeline.vue'
import { mdiPlay, mdiPause, mdiRewind } from '@mdi/js'
import accountStateMixin from '@/mixins/AccountStateMixin'
import analyticsMixin from './editor-mixins/AnalyticsMixin'
import dialogsMixin from './editor-mixins/DialogsMixin'
import unityCommunicationMixin from './editor-mixins/UnityCommunicationsMixin'
import unityErrorMixin from './editor-mixins/UnityErrorMixin'
import characterBrowserMixin from './editor-mixins/CharacterBrowserMixin'
import poseSequencesMixin from './editor-mixins/PoseSequenceMixin'
import keyframeTimelineMixin from './editor-mixins/KeyframeTimelineMixin'
import { convertBlazePose } from './poseConvertUtil'
import { copyTextToClipboard } from '@/util/clipboardUtil'
import { Day6Actions, day6Analytics } from '@/util/analyticsUtil'
import ShareLevel from '@/constants/Day6ShareLevel'
import { isEqual, cloneDeep } from 'lodash'
import { createNamespacedHelpers } from 'vuex'
import accountApi from '@/api/account'
const { mapState, mapActions } = createNamespacedHelpers('day6')

const defaultProcessingVersions = {
  human: process.env.VUE_APP_DAY6_EDITOR_HUMAN_SCHEMA_VERSION
}

export default {
  name: 'Day6Editor',
  components: {
    EditorActionBar,
    Unity3dContainer,
    FullDialog,
    Day6Info,
    MailingListDialog,
    ImagePoseProvider,
    CharacterModelBrowser,
    CharacterPoseSequencesView,
    KeyframeTimeline
  },
  props: {
    id: String
  },
  mixins: [
    accountStateMixin,
    analyticsMixin,
    dialogsMixin,
    unityCommunicationMixin,
    unityErrorMixin,
    characterBrowserMixin,
    poseSequencesMixin,
    keyframeTimelineMixin
  ],
  data: function () {
    return {
      mdiPlay,
      mdiPause,
      mdiRewind,
      commonResource,
      day6Resource,
      loadingIndicatorUi: uiConstant.loadingIndicator,
      isUnitySupported: false,
      unitySupportMessage: '',
      // TODO Use correct theme if more are supported
      unityCanvasBackground: this.$vuetify.theme.themes.light.accent,
      unityCanvasLoadOpacity: 0,
      isAppBarVisible: this.isAppBarVisible(this.$vuetify),
      isSmallScreen: this.isSmallScreenVuetify(this.$vuetify),
      isThinScreen: this.isThinScreenWidthVuetify(this.$vuetify),
      // Body dimensions (snapshots)
      defaultBodyDimensions: null,
      lastBuildBodyDimensions: null,
      bodyDimensions: {},
      activeDimensionId: null,
      dimensionsIdSummary: '',
      fileDownloadUrl: '',
      dimensionsSaveCounter: 0,
      changedIdSummary: '',
      // Pose
      characterPoseId: '',
      lastSavedCharacterPose: null,
      characterPoseCache: null,
      poseBodyDimensionsCache: null,
      poseSaveCounter: 0,
      // Preview
      previewSettingsCache: null,
      previewSettingsSaveCounter: 0,
      showPreviewPlayback: false,
      isPreviewPlayingAnimation: false,
      previewPoseSequenceCache: null,
      // Share
      shareLevel: ShareLevel.NONE,
      // Status states
      isSavingData: false,
      isBuildQueued: false,
      isProcessingBuild: false,
      pendingBuildAction: '',
      // Initial data has been retrieved from backend. Stays at false once changes. Specific to JS no bearing on Unity.
      isOpeningLoad: true,
      isShareOriginating: false,
      // Latest (account) dimension checks
      latestDimensionCheckId: null,
      latestDimensionsData: {},
      // Pose estimate
      isEstimatingPose: false,
      // Pose estimate was pressed in build body and should open in pose scene
      isPendingPoseEstimate: false,
      isColorPickerVisible: false,
      // Editor color (picker)
      selectedColor: '#FFEB3B',
      // Alerts
      hideBuildTimeEstimateAlert: false
    }
  },
  computed: {
    ...mapState([
      // 'queryDimensionsError',
      'isQueryingDimensions',
      'isQueryingPose',
      'isQueuingBuild',
      'isSavingPose',
      'isSharingCharacter',
      'queryingShareId'
    ]),
    stackEditor () {
      return this.isThinScreen
    },
    showTouchInfoUi () {
      return this.isSmallScreen && this.isUnitySupported
    },
    isDimensionsChanged () {
      return this.dimensionsChangeCounter > this.dimensionsSaveCounter
    },
    isPoseChanged () {
      return this.poseChangeCounter > this.poseSaveCounter && !this.isSmallScreenAnimation
    },
    isPreviewSettingsChanged () {
      return this.previewSettingsChangeCounter > this.previewSettingsSaveCounter
    },
    hasUnsavedChanges () {
      return (this.isEditScene && this.isDimensionsChanged) ||
              (this.isPoseScene && this.isPoseChanged) ||
              (this.isPreviewScene && this.isPreviewSettingsChanged)
    },
    isUpdatingData () {
      return this.isSavingData || this.isUpdatingCharacter || this.isUpdatingAnimation
    },
    poseCacheJson () {
      let poseJson = ''
      let dimensionsJson = ''
      if (Object.keys(this.characterPoseCache || {}).length) {
        poseJson = JSON.stringify(this.characterPoseCache)
        dimensionsJson = Object.keys(this.poseBodyDimensionsCache || {}).length ? JSON.stringify(this.poseBodyDimensionsCache) : ''
      }
      return { poseJson, dimensionsJson }
    },
    isPendingShare () {
      return this.pendingBuildAction === 'share'
    },
    isPendingDownload () {
      return this.pendingBuildAction === 'download'
    },
    colorPickerHeight () {
      return this.isSmallScreen ? 128 : 160
    },
    colorPickerWidth () {
      return this.isSmallScreen ? 192 : 240
    },
    enableAnimationFeatures () {
      return this.isAccountPaid && this.isPoseScene && this.isSceneReady
    }
  },
  watch: {
    selectedColor (val) {
      this.unityChangeColor(val)
    },
    hideBuildTimeEstimateAlert (val) {
      if (val && this.showAssetBuildTimeEstimateDialog && this.isAccountPaid) {
        this.onHideBuildTimeEstimateAlert()
      }
    },
    showAssetBuildTimeEstimateDialog (val) {
      if (!val) {
        this.focusUnity()
      }
    }
  },
  mounted () {
    const glContext = this.$refs.emptyCanvas.getContext('webgl2')
    if (glContext) {
      this.isUnitySupported = true
    } else {
      this.unitySupportMessage = commonResource.webGlNotSupported
      return
    }

    this.defaultBodyDimensions = localDefaultBodyDimensions
    this.bodyDimensions = cloneDeep(this.defaultBodyDimensions)

    this.setUnityWebGlMethods()

    this.isShareOriginating = routes.day6.isShareOpen(this.$route)

    this.refreshPaidStatus()
      .then(() => this.loadDay6())
      // TODO Defer this query until necessary
      .then(() => this.queryAccountAlerts())
      .catch(e => {
        this.accountStateErrorDialog()
        Vue.logger.error(e)
      })

    if (this.isShareOriginating) {
      this.analyzeShareRoute()
    }

    this.appEventBus.$on('day6-navigate', this.onDay6Navigate)
    this.appEventBus.$on('leave-day6', this.onNavigateFromDay6)

    document.addEventListener('keydown', this.onKeyDown)
  },
  // Route guards don't work here. Internet says it is due to being nested inside a router view.
  // unmounted is not called if component is hidden with v-if
  beforeDestroy () {
    this.unsubscribeStatusObserver()

    this.deleteUnityWebGlMethods()

    this.appEventBus.$off('day6-navigate', this.onDay6Navigate)
    this.appEventBus.$off('leave-day6', this.onNavigateFromDay6)

    document.removeEventListener('keydown', this.onKeyDown)
  },
  methods: {
    ...mapActions([
      'queryDefaultDimensions',
      'queryDimensionsData',
      'queryLatestDimensions',
      'queryCharacterPose',
      'buildBody',
      'observeStatus',
      'savePose',
      'queryPreviewSettings',
      'savePreviewSettings',
      'shareAccountCharacter',
      'queryDimensionsShareLevel',
      'querySummaryIdDimensions'
    ]),
    async quitUnity () {
      if (this.unityInstance) {
        await this.unityInstance.Quit()
        this.unityInstance = null
      }
    },
    openAccountPay () {
      // Changes will be tested on navigate from day6
      this.$router.push({ name: 'account-pay' })

      this.captureOpenAccount(true)
    },
    async onNavigateFromDay6 ({ next }) {
      if (this.hasUnsavedChanges) {
        // TODO Prompt to save unsaved before continuing
        Vue.logger.log('Not saving changes')
      }

      await this.quitUnity()

      next()
    },
    onDay6Navigate (data) {
      // TODO Review/test that this holds up against all navigation

      // Browser navigation abandons any changes
      if (this.isPoseScene) {
        this.unityToggleBuildPoseScene({})
      } else if (this.isPreviewScene) {
        this.unityPreviewOpenBodyBuild()
      }

      if (data.to === window.location.pathname) {
        if (routes.day6.isCharacterMainPath(data.to)) {
          this.openBodyDimensions('')
        } else if (routes.day6.isCharacterOpenPath(data.to)) {
          const dimensionsId = routes.day6.getCharacterOpenDimensionsId(data.to)
          this.openBodyDimensions(dimensionsId)
        } else {
          Vue.logger.log('Unrecognized back navigation', data.to)
        }
      } else {
        Vue.logger.log('day6 navigate', data, this.activeDimensionId, window.location.pathname)
      }
    },
    onKeyDown (e) {
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.stopPropagation()
        e.preventDefault()
        this.saveChanges('keyboard', true)
      }
    },
    /**
     * Kickoff point for Day6
     *
     * Loads Unity and makes backend queries.
     */
    loadDay6 () {
      this.$refs.unityContainer.loadUnity()
      this.loadDimensionsData()
    },
    // Due to canvas size mismatches for certain screen sizes...
    setConsistentCanvasSize () {
      const editorContainer = this.$refs.editorContainer
      const editorChildren = editorContainer.childNodes
      if (editorChildren.length === 2) {
        const actionBar = editorChildren[0]
        const unityContainer = editorChildren[1]
        let canvasWidth = editorContainer.clientWidth
        let canvasHeight = editorContainer.clientHeight
        if (this.stackEditor) {
          canvasHeight = editorContainer.clientHeight - actionBar.clientHeight
        } else {
          canvasWidth = editorContainer.clientWidth - actionBar.clientWidth
        }
        if (!(canvasWidth === unityContainer.clientWidth &&
              canvasHeight === unityContainer.clientHeight)) {
          Vue.logger.log(`Resizing canvas (${unityContainer.clientWidth}, ${unityContainer.clientHeight}) to match space (${canvasWidth}, ${canvasHeight})`)
          this.$refs.unityContainer.resizeCanvas({
            height: canvasHeight,
            width: canvasWidth
          })
        }
      }
    },
    /**
     * Clears build related data
     *
     * Call when data pertaining to a build has changed locally to invalidate ongoing requests.
     */
    clearBuildData () {
      this.fileDownloadUrl = ''
      this.unsubscribeStatusObserver()
      this.pendingBuildAction = undefined
    },
    resetDimensionsData () {
      this.dimensionsChangeCounter = 0
      this.dimensionsSaveCounter = 0
      this.lastBuildBodyDimensions = null
      this.bodyDimensions = {}
      this.activeDimensionId = null
      this.dimensionsIdSummary = ''
      // Summary ID of last changed character
      this.changedIdSummary = ''
      this.clearBuildData()
    },
    resetPoseCounters () {
      this.poseChangeCounter = 0
      this.poseSaveCounter = 0
    },
    resetPoseData () {
      this.resetPoseCounters()
      this.characterPoseId = ''
      this.lastSavedCharacterPose = null
      this.characterPoseCache = null
      this.poseBodyDimensionsCache = null
      this.resetAnimationData()
    },
    resetPreviewData () {
      this.previewSettingsChangeCounter = 0
      this.previewSettingsSaveCounter = 0
      this.previewSettingsCache = null
      this.showPreviewPlayback = false
      this.isPreviewPlayingAnimation = false
      this.previewPoseSequenceCache = null
    },
    onSaveDataError (error) {
      this.$handleGeneralError(error, this.$refs.fullDialog)
      this.clearBuildData()
    },
    closeBodyBuildDialogs () {
      if (this.$refs.characterBrowser) {
        this.$refs.characterBrowser.closeBrowser()
      }
      this.showDataBrowser = false
    },
    closePoseDialogs () {
      if (this.$refs.poseSequencesView) {
        this.$refs.poseSequencesView.closeView()
      }
    },
    debouceAutosaveDimensions () {
      if (this.isAccountPaid) {
        // TODO Debounce save dimensions. Build not necessary
      }
    },
    debouceAutosavePose () {
      if (this.isAccountPaid) {
        // TODO Debounce save pose.
      }
    },
    onInfoDialogAction () {
      this.showInfoDialog = true

      day6Analytics.actionPress(Day6Actions.openInfo)
    },
    onMailingListDialogAction () {
      this.showMailingListDialog = true

      day6Analytics.actionPress(Day6Actions.mailingList)
    },
    async saveChanges (source, skipFocus) {
      if (!skipFocus) {
        this.focusUnity()
        day6Analytics.onSaveChanges(source)
      }

      try {
        this.persistentDialogMessage = day6Resource.savingChanges

        if (this.isEditScene || this.isPoseScene) {
          await this.saveDirty()
        } else if (this.isPreviewScene) {
          await this.saveDirtyPreview(this.isPreviewSettingsChanged)
        }
      } finally {
        this.persistentDialogMessage = ''
      }
    },
    /**
     * Resets body dimensions to starting or default dimensions or pose to last saved or default pose
     */
    restoreBodyDimensions () {
      this.bodyDimensions = Object.assign({}, this.lastBuildBodyDimensions || this.defaultBodyDimensions)
      this.unitySyncLocalState()
      this.clearBuildData()
    },
    restoreCharacterPose () {
      this.characterPoseCache = this.lastSavedCharacterPose ? cloneDeep(this.lastSavedCharacterPose) : undefined
      this.unitySyncLocalState()
    },
    onRestoreAction () {
      if (!this.isEditScene) { return }

      this.focusUnity()
      day6Analytics.actionPress(Day6Actions.resetDimensions)

      this.restoreBodyDimensions()
    },
    // Refactored for structure. Call saveDirty().
    async _saveDirtyDimensions (isDownload) {
      // Always save dimensions in case default asset is needed for pose, preview, ...
      if (!this.isEditScene) { return }

      const changeCounter = this.dimensionsChangeCounter

      const buildRequestKey = this.unityRequestBuild()
      if (!buildRequestKey) { return }

      const bodyDimensions = await this.waitForUnityResponse(buildRequestKey)
      const buildData = {
        id: this.activeDimensionId,
        // TODO Update once other types are supported
        version: defaultProcessingVersions.human,
        bodyDimensions,
        isDownload
      }

      const { id, fileDownloadUrl, dimensionsId, buildStatusId, error } = await this.buildBody(buildData)
      if (this.activeDimensionId !== id) { return }

      if (error) { throw error }

      this.dimensionsSaveCounter = changeCounter

      let isQueued = false

      if (fileDownloadUrl) {
        this.fileDownloadUrl = fileDownloadUrl
      } else {
        this.activeDimensionId = dimensionsId
        this.fileDownloadUrl = ''

        this.observeStatusUpdates(buildStatusId)
        isQueued = true
      }

      // TODO Set dimensionsIdSummary. Response should return rather than query here to reduce number of calls.
      this.onCharacterDimensionsSaved()

      return isQueued
    },
    // Refactored for structure. Call saveDirty().
    async _saveDirtyPose () {
      // TODO Test shared posed character. Does pose save as expected for all scenarios?

      if (!this.isPoseChanged) { return }

      const changeCounter = this.poseChangeCounter

      let characterPose
      let sequenceData
      if (this.isPoseScene) {
        const poseRequestKey = this.unityRequestPose()
        if (!poseRequestKey) { return }

        // Either a pose or pose + animations + more
        const poseAnimationData = await this.waitForUnityResponse(poseRequestKey)
        if (!poseAnimationData) { return }

        characterPose = poseAnimationData.pose || poseAnimationData
        this.characterPoseCache = characterPose

        if (this.isAccountPaid) {
          const hasSequences = poseAnimationData.sequences || poseAnimationData.locomotionParameters
          const { pose, ...remaining } = poseAnimationData
          sequenceData = hasSequences ? remaining : { pose }
        }
      } else if (this.isShareOriginating && this.characterPoseCache) {
        characterPose = cloneDeep(this.characterPoseCache)
      } else {
        return
      }

      this.poseBodyDimensionsCache = cloneDeep(this.bodyDimensions)

      const savePoseResult = await this.savePose({
        dimensionsId: this.activeDimensionId,
        dimensions: this.bodyDimensions,
        poseId: this.characterPoseId,
        pose: characterPose,
        sequenceId: this.activePoseSequenceId,
        sequenceData,
        animationGroupId: this.animationGroupId
      })
      const { error, poseId, dimensionsId, poseSequenceId, animationGroupId } = savePoseResult
      if (error) { throw error }

      this.poseSaveCounter = changeCounter

      if (!this.activeDimensionId || this.activeDimensionId === dimensionsId) {
        this.activeDimensionId = dimensionsId
        this.characterPoseId = poseId
        this.lastSavedCharacterPose = cloneDeep(characterPose)

        if (this.isAccountPaid && this.isPoseScene) {
          // Set name if new pose sequence
          if (!this.activePoseSequenceId ||
              (!this.animationGroupId && this.activePoseSequenceId === poseSequenceId)) {
            const sequenceName = this.$refs.poseSequencesView.getActiveSequenceName()
            await this.syncPoseSequenceName(animationGroupId, poseSequenceId, sequenceName, true)
          }

          this.activePoseSequenceId = poseSequenceId
          this.animationGroupId = animationGroupId
        }
      } else {
        Vue.logger.error(`Dimensions changed to ${this.activeDimensionId} on pose change of ${dimensionsId}`)
      }
    },
    // TODO Return result so callers can determine whether to continue or not
    async saveDirty (continueActionKey) {
      if (this.isSavingData) { return }

      // Build dependant actions should not overlap
      if (this.pendingBuildAction && continueActionKey) {
        Vue.logger.log(`${continueActionKey} was requested while ${this.pendingBuildAction} is pending... Should not be possible. Do a code audit.`)
        return
      }
      this.pendingBuildAction = continueActionKey

      this.isSavingData = true
      let isQueued = false
      try {
        isQueued = await this._saveDirtyDimensions(this.isPendingDownload)
        await this._saveDirtyPose()

        this.changedIdSummary = this.dimensionsIdSummary
      } catch (error) {
        this.onSaveDataError(error)
      } finally {
        this.isSavingData = false

        if (!isQueued &&
            // Pending share clears when link is copied
            !this.isPendingShare) {
          this.pendingBuildAction = undefined
        }
      }
    },
    async saveDirtyPreview (refreshSettings) {
      if (!(this.activeDimensionId && this.isPreviewSettingsChanged)) { return }

      const changeCounter = this.previewSettingsChangeCounter

      if (refreshSettings) {
        const previewRequestKey = this.unityRequestPreview()
        if (!previewRequestKey) { return }

        const previewSettings = await this.waitForUnityResponse(previewRequestKey)
        if (!previewSettings) { return }
        this.previewSettingsCache = previewSettings
      }

      if (!this.previewSettingsCache) { return }

      const saveState = cloneDeep(this.previewSettingsCache)
      const saved = await this.savePreviewSettings({
        dimensionsId: this.activeDimensionId,
        previewSettings: saveState
      })
      if (saved && isEqual(saveState, this.previewSettingsCache)) {
        this.previewSettingsSaveCounter = changeCounter
      }
    },
    buildActionFollowThrough () {
      if (!this.pendingBuildAction) { return }

      const pendingAction = this.pendingBuildAction
      this.pendingBuildAction = undefined

      if (this.fileDownloadUrl) {
        switch (pendingAction) {
          case 'toggle-build-pose':
            this.continueToBuildPose()
            break
          case 'download':
            this.continueToDownload()
            break
          case 'preview':
            this.continueToPreview()
            break
          default:
            Vue.logger.log(`Unknown build action ${pendingAction}. Is logic outdated?`)
            break
        }
      }
    },
    onToggleBuildPoseAction (promptSaveDirty) {
      if (this.isUpdatingData ||
          this.isOpeningLoad ||
          this.isUnityLoading
      ) {
        this.focusUnity()
        return
      }

      if (this.isPoseScene) {
        this.isEstimatingPose = false
      }

      if (this.isSurveyRequested) {
        this.focusUnity()
      } else {
        this.surveyDialog()
      }
      const analyticsAction = this.isPoseScene ? Day6Actions.openBuildBody : Day6Actions.openPose
      day6Analytics.actionPress(analyticsAction)

      // TODO Prompt for body dimensions change but must take asset URL into account
      if (promptSaveDirty && (this.isPoseScene && this.isPoseChanged)) {
        this.toggleBuildPoseSaveChangesDialog()
        return
      }

      this.saveDirty('toggle-build-pose').then(() => {
        if (!this.pendingBuildAction) {
          this.continueToBuildPose()
        }
      })
    },
    continueToBuildPose () {
      this.unityToggleBuildPoseScene(this.poseCacheJson)
    },
    /**
     * Opens new window to download file
     *
     * Action to download should only be enabled if {@code fileDownloadUrl} is defined.
     */
    onStartDownloadAction () {
      if (!this.isAccountPaid) {
        this.paidFeatureDialog(day6Resource.assetDownloaPaidFeature)
        return
      }

      this.focusUnity()
      day6Analytics.actionPress(Day6Actions.downloadAsset)

      this.saveDirty('download').then(() => {
        if (!this.pendingBuildAction) {
          this.continueToDownload()
        }

        // TODO Show message on other builds with shortened time message
        if (this._statusObserver) {
          const accountAlerts = this.accountPreferences.alerts || {}
          if (!(this.hideBuildTimeEstimateAlert || accountAlerts.hideDay6BuildEst)) {
            this.showAssetBuildTimeEstimateDialog = true
          }
        }
      })
    },
    continueToDownload () {
      const downloadUrl = this.fileDownloadUrl
      // Dimensions could have changed during build
      if (downloadUrl) {
        window.open(downloadUrl, '_blank')
      }
    },
    /**
     * Opens new window to preview generated file
     *
     * Action to preview should only be enabled if {@code fileDownloadUrl} is defined.
     */
    onPreviewGeneratedAction () {
      this.focusUnity()
      day6Analytics.actionPress(Day6Actions.openPreview)

      this.saveDirty('preview').then(() => {
        if (!this.pendingBuildAction) {
          this.continueToPreview()
        }
      })
    },
    continueToPreview () {
      const assetUrl = this.fileDownloadUrl
      if (assetUrl) {
        this.unityPreviewAsset(assetUrl, this.poseCacheJson)
      } else {
        this.previewNotSupportedDialog()
        Vue.logger.log('No previews')
      }
    },
    onShareCharacter () {
      this.focusUnity()

      if (this.isPendingShare || this.isSharingCharacter) { return }

      day6Analytics.actionPress(Day6Actions.shareLink)

      this.saveDirty('share')
        .then(() => this.saveDirtyPreview(true))
        .then(() => this.continueToShare())
    },
    async continueToShare () {
      // Default dimensions and pose copies default share link
      if (!this.activeDimensionId) {
        this.copyShareLink('')
        this.queryShareLevel()
        return
      }

      const { dimensionsId, shareId } = await this.shareAccountCharacter({
        dimensionsId: this.activeDimensionId,
        shareLevel: ShareLevel.PUBLIC
      })
      if (shareId && dimensionsId === this.activeDimensionId) {
        this.copyShareLink(shareId)
      }
      this.queryShareLevel()
    },
    async copyShareLink (shareId) {
      const link = routes.day6.linkToExisting(shareId, this.analyticsShareCount)

      const copied = await copyTextToClipboard(link)
      if (copied) {
        this.showSnackbar(day6Resource.copiedToClipboard)
      } else {
        // TODO Show error dialog that share only works if permission is granted and tab remains open until share is copied
      }

      if (this.isPendingShare) {
        this.pendingBuildAction = undefined
      }
    },
    cancelPoseEstimate () {
      this.isEstimatingPose = false
      this.isPendingPoseEstimate = false
    },
    onEstimatePose () {
      day6Analytics.actionPress(Day6Actions.estimatePose)
      this.focusUnity()

      if (this.isPoseScene) {
        this.showPoseEstimateDialog = true
      } else {
        this.isPendingPoseEstimate = true
        this.onToggleBuildPoseAction()
      }
    },
    onPoseEstimateResult ({ data }) {
      this.showPoseEstimateDialog = false

      // TODO Manage isEstimatingPose state proper since could be (re)estimating at any time. For example an estimation is processing but another is started. The previous could finish before the new one. Would it hijack the ongoing estimation?
      if (!this.isEstimatingPose) { return }

      try {
        const hasPoseData = Object.keys(data || {}).length
        const convertedPose = hasPoseData ? convertBlazePose(data, 0.2) : {}
        if (convertedPose.body) {
          this.unityApplyPoseEstimate(convertedPose)
        } else {
          this.showOkDialog(poseEstimateResource.poseNotFoundTryAgain)
        }
      } catch (e) {
        // TODO Show issue with pose detection
        Vue.logger.error(e)
      } finally {
        this.isEstimatingPose = false
      }
    },
    onPreviewRewind () {
      this.focusUnity()

      this.unityPreviewRewindAnimation()
    },
    onPreviewTogglePlayPause () {
      this.focusUnity()

      this.unityPreviewTogglePlay()
    },
    async onHideBuildTimeEstimateAlert () {
      await accountApi.saveAlertHideDay6BuildTimeEstimate(this.hideBuildTimeEstimateAlert)
    },
    /**
     * Route to open body dimensions by ID
     */
    openBodyDimensions (dimensionsId) {
      this.resetDimensionsData()
      this.resetPoseData()
      this.resetPreviewData()
      this.shareLevel = ShareLevel.NONE
      this.isOpeningLoad = true

      routes.day6.navigateToCharacter(this.$router, dimensionsId)

      const loadingMessage = this.loadingCharacterMessage || day6Resource.loadingDotDotDot
      this.runInLoadingDialog(
        this.loadDimensionsData,
        loadingMessage,
        () => { this.loadingCharacterMessage = '' })
    },
    /**
     * Queries, saves, and optionally loads default dimensions
     *
     * @param loadInEditor Loads dimensions into editor if TRUE
     * @param characterType Character type dimensions to load
     */
    async queryLoadDefaultDimensions (loadInEditor, characterType = 'human') {
      const dimensions = await this.queryDefaultDimensions(characterType)
      if (dimensions && Object.keys(dimensions).length) {
        // TODO Revisit when other types are supported
        this.defaultBodyDimensions = dimensions
        if (loadInEditor) {
          this.restoreBodyDimensions()
        }
      } else {
        Vue.logger.error(`Latest ${characterType} default dimensions not found`)
      }
    },
    /**
     * Performs various actions on dimensions data
     *
     * - Queries, saves, and syncs with editor default dimensions on opening load.
     * - Queries dimensions data if existing or active dimensions ID
     *   - Assumes existing data if successful
     *   - Sets file download URL if successful and defined
     */
    async loadDimensionsData () {
      // Cache defaults if not yet loaded
      if (this.isOpeningLoad) {
        await this.queryLoadDefaultDimensions(true)
      }

      const loadId = this.activeDimensionId ? this.activeDimensionId : this.id
      const {
        id,
        idSummary,
        fileDownloadUrl,
        dimensions,
        idSwap,
        shared
      } = await this.queryDimensionsData({
        id: loadId,
        checkForShare: this.isOpeningLoad,
        isPlusDownload: this.isPendingDownload
      })

      let syncOnComplete = true
      try {
        // New character no further action necessary
        if (!loadId) {
          return
        }

        // Invalid ID in link, redirect to new editor
        if (!(dimensions || this.activeDimensionId || shared)) {
          this.invalidDimensionsDialog()
          syncOnComplete = false
          return
        }

        // Assume shared state only on first load when character has not been copied
        if (!this.activeDimensionId && shared) {
          this.bodyDimensions = shared.bodyDimensions
          this.fileDownloadUrl = shared.fileDownloadUrl
          this.characterPoseCache = shared.characterPose
          this.poseBodyDimensionsCache = shared.poseBodyDimensions
          this.previewSettingsCache = shared.previewSettings
          this.previewPoseSequenceCache = shared.poseSequenceData
          this.lastBuildBodyDimensions = cloneDeep(shared.bodyDimensions)
          if (shared.characterPose) {
            this.lastSavedCharacterPose = cloneDeep(shared.characterPose)
          }

          // Shared character should save as new
          this.dimensionsChangeCounter = this.dimensionsSaveCounter + 1
          this.poseChangeCounter = this.poseSaveCounter + 1
          return
        }

        // 1. Share posed with public always opens to preview
        // 2. Share posed with account opens to preview unless own character which opens to body build
        if (this.isAuthenticated || !this.isOpeningLoad) {
          this.isShareOriginating = false
        }

        const backendDimensionsId = idSwap || id
        const snapshotBodyDimensions = dimensions ? dimensions.bodyDimensions : null
        // Assume data if existing dimensions were loaded
        if (this.id && !this.activeDimensionId && id === this.id) {
          this.activeDimensionId = backendDimensionsId
          this.bodyDimensions = snapshotBodyDimensions

          if (this.isOpeningLoad) {
            this.lastBuildBodyDimensions = cloneDeep(snapshotBodyDimensions)
            const { idPose, idPreviewSettings, idAnimations } = dimensions

            if (idPose) {
              const { characterPose, poseBodyDimensions } = await this.queryCharacterPose(idPose)
              if (characterPose) {
                this.characterPoseId = idPose
                this.characterPoseCache = characterPose
                this.poseBodyDimensionsCache = poseBodyDimensions
                this.lastSavedCharacterPose = cloneDeep(characterPose)
              }
            }

            if (idPreviewSettings) {
              const { previewSettings } = await this.queryPreviewSettings(idPreviewSettings)
              if (previewSettings) {
                this.previewSettingsCache = previewSettings
              }
            }

            this.animationGroupId = idAnimations

            this.queryShareLevel()

            this.loadCharacterAnimations()
          }

          // Alert if loaded dimensions is not the most current
          if (this.latestDimensionCheckId !== backendDimensionsId) {
            this.latestDimensionCheckId = backendDimensionsId
            const { latestId } = await this.queryLatestDimensions(backendDimensionsId)
            if (latestId) {
              this.latestDimensionsData[backendDimensionsId] = { latestId }
              this.loadLatestDimensionsDialog()
            }
          }
        }

        if (!this.isOpeningLoad) {
          // In this state syncing is not necessary as local dimensions may be newer and should not be overwritten with what was saved/built
          syncOnComplete = false
        }

        this.dimensionsIdSummary = idSummary

        const isMatchingId = backendDimensionsId === this.activeDimensionId
        this.fileDownloadUrl = isMatchingId && isEqual(this.bodyDimensions, snapshotBodyDimensions) ? fileDownloadUrl : ''
        if (this.fileDownloadUrl) {
          this.lastBuildBodyDimensions = cloneDeep(snapshotBodyDimensions)
        }
      } finally {
        // Opening load is over after first query
        this.isOpeningLoad = false

        if (syncOnComplete) {
          this.unitySyncLocalState()
        }

        this.buildActionFollowThrough()

        this.queryShareLevel()
      }
    },
    async queryPreviewPoseSequenceCache () {
      if (this.isAccountPaid) {
        // TODO Test both cases work as expected
        let poseSequenceId = this.activePoseSequenceId
        if (!poseSequenceId && this.activeDimensionId) {
          ({ activeSequence: poseSequenceId } = await this.queryCharacterAnimations(this.activeDimensionId))
        }

        let poseSequence
        if (poseSequenceId) {
          ({ data: poseSequence } = await this.queryPoseSequence(this.activePoseSequenceId))
        }

        if (poseSequence) {
          this.previewPoseSequenceCache = poseSequence
          this.unityPreviewLoadAnimation()
        }
      } else if (this.activeDimensionId) {
        // Clear shared pose sequence once body dimensions are saved
        this.previewPoseSequenceCache = null
      }
    },
    /**
     * Begins validation of generated file for download/preview
     */
    async validateDownload () {
      this.loadDimensionsData()
    },
    /**
     * Unsubscribes from job status observer if previously subscribed
     */
    unsubscribeStatusObserver () {
      if (this._statusObserver) {
        this._statusObserver()
        this._statusObserver = null
        this.isBuildQueued = false
        this.isProcessingBuild = false
      }
    },
    /**
     * Observes active job state updating local state accordingly
     *
     * Observer unsubscription is saved to {@code _statusObserver}.
     */
    async observeStatusUpdates (statusId) {
      this.unsubscribeStatusObserver()
      // Seems mapAction returns Promise even if async is not specified in the store action.
      // Might as well be explicit in this so surprises don't pop up.
      this._statusObserver = await this.observeStatus({
        statusId,
        docObserver: snap => {
          const { exin, state } = snap.data()
          const { idSource } = exin
          if (idSource === this.activeDimensionId) {
            this.isBuildQueued = ['start', 'waiting'].includes(state)
            this.isProcessingBuild = state === 'processing'
            if (state === 'end') {
              this.validateDownload()
            }
          }
        }
      })
    },
    async queryShareLevel () {
      const dimensionId = this.activeDimensionId || this.id
      if (!dimensionId) {
        this.shareLevel = ShareLevel.NONE
        return
      }

      const shareLevel = await this.queryDimensionsShareLevel(dimensionId)
      if (dimensionId === this.activeDimensionId && !this.isPendingShare) {
        this.shareLevel = shareLevel
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.main-container {
  display: flex;
  justify-content: center;

  > * {
    padding: 0;
  }

  > .empty-content {
    align-self: center;
  }

  >.editor-interface {
    display: flex;
    flex-direction: row;
    width: 100%;

    &.stack-inner {
      flex-direction: column-reverse;
    }

    &:not(.stack-inner) {
      >.editor-actions {
        height: 100%;
      }
    }

    >.unity-container {
      flex-grow: 1;
    }
  }

  #canvas-interior {
    position: absolute;
    width: 100%;
    height: 100%;
    pointer-events: none;

    #color-picker {
      margin: 16px;
      pointer-events: painted;

      &.bottom-gravity {
        position: absolute;
        bottom: 0;
      }

      &.top-space {
        margin-top: 80px;
      }
    }
  }
}

.loading-modal {
  padding: 16px;
  background-color: white;
}

.data-browser-frame {
  background-color: white;
  overflow: hidden;
}

.pose-sequences-view {
  margin: 16px;
  position: absolute;
  bottom: 0;
}

.keyframe-timeline-container {
  position: absolute;
  width: 100%;
  top: 0;
  padding-left: 128px;

  .keyframe-timeline-inner {
    display: flex;

    .keyframe-timeline {
      width: 50%;
      height: 48px;
      border-radius: 4px;
      overflow: hidden;
    }
  }
}

.preview-playback-container {
  position: absolute;
  width: 100%;
  bottom: 0;

  .preview-playback-inner {
    display: flex;

    .preview-playback-actions {
      margin: 16px;
      padding: 8px;
      background: white;
      // TODO Use variables for diemnsions
      border-radius: 4px;
    }
  }
}

</style>
